import React, { useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import DropdownArrowIcon from '@grnhse/seedling/lib/birch/icons/dropdown_arrow';
import IconButton from '@grnhse/seedling/lib/birch/components/buttons/icon_buttons';
import type { ScopeInfo, ScopeGroup, ScopeCategory } from './scope_utils';
import {
  ProductHeader,
  ArrowIconWrapper,
  Badge,
  ReadBadge,
  WriteBadge,
  AccessTypeHeader,
  CategoryHeader,
  ScopeItemWrapper,
  Divider,
} from './styled_components';

type CategoryCheckboxesProps = {
  category: ScopeCategory;
  selectedScopes: Set<string>;
  onToggleScope: (name: string) => void;
  onToggleCategory: (scopes: ScopeInfo[], checked: boolean) => void;
};

function CategoryCheckboxes({
  category,
  selectedScopes,
  onToggleScope,
  onToggleCategory,
}: CategoryCheckboxesProps) {
  const selectedCount = category.scopes.filter((s) =>
    selectedScopes.has(s.name)
  ).length;
  const allSelected = selectedCount === category.scopes.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <div>
      <CategoryHeader>
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(checked: boolean) => {
            onToggleCategory(category.scopes, checked);
          }}
          label={category.categoryLabel}
          value={category.category}
        />
      </CategoryHeader>
      <ScopeItemWrapper>
        <Stack spacing={1}>
          {category.scopes.map((scope) => (
            <Checkbox
              key={scope.name}
              checked={selectedScopes.has(scope.name)}
              onChange={() => onToggleScope(scope.name)}
              label={scope.description}
              value={scope.name}
            />
          ))}
        </Stack>
      </ScopeItemWrapper>
    </div>
  );
}

type AccessGroupProps = {
  accessType: 'read' | 'write';
  categories: ScopeCategory[];
  selectedScopes: Set<string>;
  onToggleScope: (name: string) => void;
  onToggleCategory: (scopes: ScopeInfo[], checked: boolean) => void;
  translationFn: (key: string) => string;
};

function AccessGroupSection({
  accessType,
  categories,
  selectedScopes,
  onToggleScope,
  onToggleCategory,
  translationFn,
}: AccessGroupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const allScopes = categories.flatMap((c) => c.scopes);
  const selectedCount = allScopes.filter((s) =>
    selectedScopes.has(s.name)
  ).length;
  const allSelected = selectedCount === allScopes.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const BadgeComponent = accessType === 'read' ? ReadBadge : WriteBadge;
  const label =
    accessType === 'read'
      ? translationFn('scope_groups.read_access')
      : translationFn('scope_groups.write_access');

  return (
    <div>
      <AccessTypeHeader>
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(checked: boolean) => {
              onToggleCategory(allScopes, checked);
            }}
            label=""
            value={accessType}
          />
        </div>
        <BadgeComponent>{label}</BadgeComponent>
        <ArrowIconWrapper isOpen={isOpen}>
          <IconButton
            icon={DropdownArrowIcon}
            label={`Toggle ${label}`}
            onClick={() => setIsOpen(!isOpen)}
          />
        </ArrowIconWrapper>
      </AccessTypeHeader>
      <Collapsible id={`access-group-${accessType}`} isOpen={isOpen}>
        <Stack spacing={1} sx={{ marginTop: 1, paddingLeft: '24px' }}>
          {categories.map((category) => (
            <CategoryCheckboxes
              key={category.category}
              category={category}
              selectedScopes={selectedScopes}
              onToggleScope={onToggleScope}
              onToggleCategory={onToggleCategory}
            />
          ))}
        </Stack>
      </Collapsible>
    </div>
  );
}

type ScopeGroupSectionProps = {
  group: ScopeGroup;
  selectedScopes: Set<string>;
  onToggleScope: (name: string) => void;
  onToggleSubGroup: (scopeItems: ScopeInfo[], checked: boolean) => void;
  onToggleProduct: (sg: ScopeGroup, checked: boolean) => void;
  translationFn: (key: string) => string;
};

export function ScopeGroupSection({
  group: scopeGroup,
  selectedScopes,
  onToggleScope,
  onToggleSubGroup,
  onToggleProduct,
  translationFn,
}: ScopeGroupSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const allScopes = [...scopeGroup.readScopes, ...scopeGroup.writeScopes];
  const selectedCount = allScopes.filter((s) =>
    selectedScopes.has(s.name)
  ).length;
  const allSelected = selectedCount === allScopes.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const readSelectedCount = scopeGroup.readScopes.filter((s) =>
    selectedScopes.has(s.name)
  ).length;
  const writeSelectedCount = scopeGroup.writeScopes.filter((s) =>
    selectedScopes.has(s.name)
  ).length;
  const hasReadAccess = readSelectedCount > 0;
  const hasWriteAccess = writeSelectedCount > 0;

  const badgeLabel =
    hasReadAccess && hasWriteAccess
      ? translationFn('scope_groups.read_write_access_enabled')
      : hasReadAccess
      ? translationFn('scope_groups.read_access_enabled')
      : hasWriteAccess
      ? translationFn('scope_groups.write_access_enabled')
      : null;

  return (
    <div>
      <Divider />
      <ProductHeader>
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(checked: boolean) => {
              onToggleProduct(scopeGroup, checked);
            }}
            label={scopeGroup.productLabel}
            value={scopeGroup.product}
          />
        </div>
        {!isOpen && badgeLabel && <Badge>{badgeLabel}</Badge>}
        <ArrowIconWrapper isOpen={isOpen}>
          <IconButton
            icon={DropdownArrowIcon}
            label={`Toggle ${scopeGroup.productLabel}`}
            onClick={() => setIsOpen(!isOpen)}
          />
        </ArrowIconWrapper>
      </ProductHeader>
      <Collapsible id={`scope-group-${scopeGroup.product}`} isOpen={isOpen}>
        <Stack spacing={2} sx={{ marginTop: 1, paddingLeft: '8px' }}>
          {scopeGroup.accessGroups.map((ag) => (
            <AccessGroupSection
              key={ag.accessType}
              accessType={ag.accessType}
              categories={ag.categories}
              selectedScopes={selectedScopes}
              onToggleScope={onToggleScope}
              onToggleCategory={onToggleSubGroup}
              translationFn={translationFn}
            />
          ))}
        </Stack>
      </Collapsible>
    </div>
  );
}
